/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import 'prismjs';
import 'prismjs/components/prism-javascript';
import {
  $createCodeNode,
  $isCodeNode,
  CODE_LANGUAGE_FRIENDLY_NAME_MAP,
  CODE_LANGUAGE_MAP,
  getLanguageFriendlyName,
} from '@lexical/code';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
} from '@lexical/list';
import { INSERT_EMBED_COMMAND } from '@lexical/react/LexicalAutoEmbedPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
  HeadingTagType,
} from '@lexical/rich-text';
import {
  $getSelectionStyleValueForProperty,
  $isParentElementRTL,
  $patchStyleText,
  $setBlocksType,
} from '@lexical/selection';
import { $isTableNode, $isTableSelection } from '@lexical/table';
import {
  $findMatchingParent,
  $getNearestBlockElementAncestorOrThrow,
  $getNearestNodeOfType,
  $isEditorIsNestedEditor,
  mergeRegister,
} from '@lexical/utils';
import {
  $createParagraphNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_NORMAL,
  ElementFormatType,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  KEY_MODIFIER_COMMAND,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import * as React from 'react';
import { Dispatch, useCallback, useContext, useEffect, useState } from 'react';
import { IS_APPLE } from 'shared/environment';

import useModal from '../../hooks/useModal';
import DropDown, { DropDownItem } from '../../ui/DropDown';
import DropdownColorPicker from '../../ui/DropdownColorPicker';
import { getSelectedNode } from '../../utils/getSelectedNode';
import { sanitizeUrl } from '../../utils/url';
import { EmbedConfigs } from '../AutoEmbedPlugin';
import { InsertImageDialog } from '../ImagesPlugin';
import InsertLayoutDialog from '../LayoutPlugin/InsertLayoutDialog';
import { InsertTableDialog } from '../TablePlugin';
import FontSize from './fontSize';
import { InsertFirmwareTableDialog } from '../FirmwareTablePlugin';
import Reset from 'assets/icons/reset.svg?react';
import styles from './toolbar.module.scss';
import clsx from 'clsx';
import ExportIcon from 'assets/icons/export.svg';
import ExportIconReact from 'assets/icons/export.svg?react';
import AITranslationIcon from 'assets/icons/AI-translation.svg';
import AITranslationIconReact from 'assets/icons/AI-translation.svg?react';
import TranslateIcon from 'assets/icons/translate-icon.svg?react';
import { LanguageSelector } from './LanguageSelector.tsx';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { PageDesignerContext } from '../../../DesignerNewPage.tsx';
import { Links } from '../../../../../../application/constants/links.ts';
import { useScopedTranslation } from '../../../../../../application/localization/useScopedTranslation.ts';
import { DefaultLanguageModal } from './DefaultLanguageModal.tsx';
import { Tip } from '../../../../../../components/uikit/Tip.tsx';
import { FeatureFlags } from '../../../../../../application/constants/env-variables.ts';
import { $isLocalizableTextNode } from '../../nodes/Localizable/LocalizableTextNode.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { CmsPageDto, CmsPageQuery, Language } from 'services/api/api-client.ts';
import { $isLocalizationLinkNode } from '../../nodes/Localizable/LocalizationLinkNode.ts';
import { $isTabItemNode } from '../../nodes/TabItemNode.ts';
import { $isProtectedParagraphNode } from '../../nodes/Localizable/ProtectedParagraphNode.ts';
import { useSettings } from '../../context/SettingsContext.tsx';
import { INSERT_COLLAPSIBLE_COMMAND } from '../CollapsiblePlugin';
import { tDynamic } from '../../../../../../helpers/tDynamic.ts';

const blockTypeToBlockName = {
  bullet: 'Bulleted List',
  check: 'Check List',
  code: 'Code Block',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  number: 'Numbered List',
  ['protected-paragraph']: 'Normal',
  quote: 'Quote',
};

const rootTypeToRootName = {
  root: 'Root',
  table: 'Table',
};

function getCodeLanguageOptions(): [string, string][] {
  const options: [string, string][] = [];

  for (const [lang, friendlyName] of Object.entries(
    CODE_LANGUAGE_FRIENDLY_NAME_MAP,
  )) {
    options.push([lang, friendlyName]);
  }

  return options;
}

const CODE_LANGUAGE_OPTIONS = getCodeLanguageOptions();

const FONT_FAMILY_OPTIONS: [string, string][] = [
  ['Noto Sans', 'Noto Sans'],
  ['Arial', 'Arial'],
  ['Courier New', 'Courier New'],
  ['Georgia', 'Georgia'],
  ['Times New Roman', 'Times New Roman'],
  ['Trebuchet MS', 'Trebuchet MS'],
  ['Verdana', 'Verdana'],
];

const FONT_SIZE_OPTIONS: [string, string][] = [
  ['10px', '10px'],
  ['11px', '11px'],
  ['12px', '12px'],
  ['13px', '13px'],
  ['14px', '14px'],
  ['15px', '15px'],
  ['16px', '16px'],
  ['17px', '17px'],
  ['18px', '18px'],
  ['19px', '19px'],
  ['20px', '20px'],
];

const ELEMENT_FORMAT_OPTIONS: {
  [key in Exclude<ElementFormatType, ''>]: {
    icon: string;
    iconRTL: string;
    name: string;
  };
} = {
  center: {
    icon: 'center-align',
    iconRTL: 'center-align',
    name: 'Center Align',
  },
  end: {
    icon: 'right-align',
    iconRTL: 'left-align',
    name: 'End Align',
  },
  justify: {
    icon: 'justify-align',
    iconRTL: 'justify-align',
    name: 'Justify Align',
  },
  left: {
    icon: 'left-align',
    iconRTL: 'left-align',
    name: 'Left Align',
  },
  right: {
    icon: 'right-align',
    iconRTL: 'right-align',
    name: 'Right Align',
  },
  start: {
    icon: 'left-align',
    iconRTL: 'right-align',
    name: 'Start Align',
  },
};

function dropDownActiveClass(active: boolean) {
  if (active) {
    return 'active dropdown-item-active';
  } else {
    return '';
  }
}

const ExportTooltipBody = () => {
  const i18n = useScopedTranslation('Page.PageDesigner');

  return (
    <div>
      <div className={styles.marginBottom4}>
        {i18n.t('upload_localization')}
      </div>
      <div className={styles.description}>
        {i18n.t('upload_localization_description')}
      </div>
    </div>
  );
};

const ImportTooltipBody = () => {
  const i18n = useScopedTranslation('Page.PageDesigner');

  return (
    <div>
      <div className={styles.marginBottom4}>
        {i18n.t('download_localization')}
      </div>
      <div className={styles.description}>
        {i18n.t('download_localization_description')}
      </div>
    </div>
  );
};

function BlockFormatDropDown({
  editor,
  blockType,
  rootType,
  disabled = false,
}: {
  blockType: keyof typeof blockTypeToBlockName;
  rootType: keyof typeof rootTypeToRootName;
  editor: LexicalEditor;
  disabled?: boolean;
}): React.JSX.Element {
  const i18n = useScopedTranslation('Page.PageDesigner');

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatHeading = (headingSize: HeadingTagType) => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      });
    }
  };

  const formatBulletList = () => {
    if (blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const formatCheckList = () => {
    if (blockType !== 'check') {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const formatNumberedList = () => {
    if (blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();
        $setBlocksType(selection, () => $createQuoteNode());
      });
    }
  };

  const formatCode = () => {
    if (blockType !== 'code') {
      editor.update(() => {
        let selection = $getSelection();

        if (selection !== null) {
          if (selection.isCollapsed()) {
            $setBlocksType(selection, () => $createCodeNode());
          } else {
            const textContent = selection.getTextContent();
            const codeNode = $createCodeNode();
            selection.insertNodes([codeNode]);
            selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.insertRawText(textContent);
            }
          }
        }
      });
    }
  };

  return (
    <DropDown
      disabled={disabled}
      buttonClassName="toolbar-item block-controls"
      buttonIconClassName={'icon block-type ' + blockType}
      buttonLabel={blockTypeToBlockName[blockType]}
      buttonAriaLabel="Formatting options for text style"
    >
      <DropDownItem
        className={
          'item ' + dropDownActiveClass(blockType === 'protected-paragraph')
        }
        onClick={formatParagraph}
      >
        <i className="icon paragraph" />
        <span className="text">{i18n.t('normal')}</span>
      </DropDownItem>
      <Divider />
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'h1')}
        onClick={() => formatHeading('h1')}
      >
        <i className="icon h1" />
        <span className="text">{i18n.t('heading')} 1</span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'h2')}
        onClick={() => formatHeading('h2')}
      >
        <i className="icon h2" />
        <span className="text">{i18n.t('heading')} 2</span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'h3')}
        onClick={() => formatHeading('h3')}
      >
        <i className="icon h3" />
        <span className="text">{i18n.t('heading')} 3</span>
      </DropDownItem>
      <Divider />
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'bullet')}
        onClick={formatBulletList}
      >
        <i className="icon bullet-list" />
        <span className="text">
          {i18n.t('bullet')} {i18n.t('list')}
        </span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'number')}
        onClick={formatNumberedList}
      >
        <i className="icon numbered-list" />
        <span className="text">
          {i18n.t('numbered')} {i18n.t('list')}
        </span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'check')}
        onClick={formatCheckList}
      >
        <i className="icon check-list" />
        <span className="text">
          {i18n.t('check')} {i18n.t('list')}
        </span>
      </DropDownItem>
      <Divider />
      {/*<DropDownItem*/}
      {/*  className={'item ' + dropDownActiveClass(blockType === 'quote')}*/}
      {/*  onClick={formatQuote}*/}
      {/*>*/}
      {/*  <i className="icon quote" />*/}
      {/*  <span className="text">{i18n.t('quote')}</span>*/}
      {/*</DropDownItem>*/}
      {/*<DropDownItem*/}
      {/*  className={'item ' + dropDownActiveClass(blockType === 'code')}*/}
      {/*  onClick={formatCode}*/}
      {/*>*/}
      {/*  <i className="icon code" />*/}
      {/*  <span className="text">{i18n.t('code')}</span>*/}
      {/*</DropDownItem>*/}
    </DropDown>
  );
}

function Divider(): React.JSX.Element {
  return <div className="divider" />;
}

function FontDropDown({
  editor,
  value,
  style,
  disabled = false,
}: {
  editor: LexicalEditor;
  value: string;
  style: string;
  disabled?: boolean;
}): React.JSX.Element {
  const handleClick = useCallback(
    (option: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if (selection !== null) {
          $patchStyleText(selection, {
            [style]: option,
          });
        }
      });
    },
    [editor, style],
  );

  const buttonAriaLabel =
    style === 'font-family'
      ? 'Formatting options for font family'
      : 'Formatting options for font size';

  return (
    <DropDown
      disabled={disabled}
      buttonClassName={'toolbar-item ' + style}
      buttonLabel={value}
      buttonIconClassName={
        style === 'font-family' ? 'icon block-type font-family' : ''
      }
      buttonAriaLabel={buttonAriaLabel}
    >
      {(style === 'font-family' ? FONT_FAMILY_OPTIONS : FONT_SIZE_OPTIONS).map(
        ([option, text]) => (
          <DropDownItem
            className={`item ${dropDownActiveClass(value === option)} ${
              style === 'font-size' ? 'fontsize-item' : ''
            }`}
            onClick={() => handleClick(option)}
            key={option}
          >
            <span className="text">{text}</span>
          </DropDownItem>
        ),
      )}
    </DropDown>
  );
}

function ElementFormatDropdown({
  editor,
  value,
  isRTL,
  disabled = false,
}: {
  editor: LexicalEditor;
  value: ElementFormatType;
  isRTL: boolean;
  disabled: boolean;
}) {
  const i18n = useScopedTranslation('Page.PageDesigner');

  const formatOption = ELEMENT_FORMAT_OPTIONS[value || 'left'];
  return (
    <DropDown
      disabled={disabled}
      buttonLabel={formatOption.name}
      buttonIconClassName={`icon ${
        isRTL ? formatOption.iconRTL : formatOption.icon
      }`}
      buttonClassName="toolbar-item spaced alignment"
      buttonAriaLabel="Formatting options for text alignment"
    >
      <DropDownItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
        }}
        className={`item ${
          formatOption.icon === 'left-align' && 'dropdown-item-active'
        }`}
      >
        <i className="icon left-align" />
        <span className="text">
          {i18n.t('left')} {i18n.t('align')}
        </span>
      </DropDownItem>
      <DropDownItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
        }}
        className={`item ${
          formatOption.icon === 'center-align' && 'dropdown-item-active'
        }`}
      >
        <i className="icon center-align" />
        <span className="text">
          {i18n.t('center')} {i18n.t('align')}
        </span>
      </DropDownItem>
      <DropDownItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
        }}
        className={`item ${
          formatOption.icon === 'right-align' && 'dropdown-item-active'
        }`}
      >
        <i className="icon right-align" />
        <span className="text">
          {i18n.t('right')} {i18n.t('align')}
        </span>
      </DropDownItem>
      <Divider />
      <DropDownItem
        onClick={() => {
          editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
        }}
        className="item"
      >
        <i className={'icon ' + (isRTL ? 'indent' : 'outdent')} />
        <span className="text">{i18n.t('outdent')}</span>
      </DropDownItem>
      <DropDownItem
        onClick={() => {
          editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
        }}
        className="item"
      >
        <i className={'icon ' + (isRTL ? 'outdent' : 'indent')} />
        <span className="text">{i18n.t('indent')}</span>
      </DropDownItem>
    </DropDown>
  );
}

const New = 'new';

export default function ToolbarPlugin({
  setIsLinkEditMode,
}: {
  setIsLinkEditMode: Dispatch<boolean>;
}): React.JSX.Element {
  const [editor] = useLexicalComposerContext();
  const isLexicalEditable = useLexicalEditable();
  const queryClient = useQueryClient();
  const pageDesignerContext = useContext(PageDesignerContext);
  const i18n = useScopedTranslation('Page.PageDesigner');

  const [activeEditor, setActiveEditor] = useState(editor);
  const [blockType, setBlockType] = useState<keyof typeof blockTypeToBlockName>(
    'protected-paragraph',
  );
  const [rootType, setRootType] =
    useState<keyof typeof rootTypeToRootName>('root');
  const [selectedElementKey, setSelectedElementKey] = useState<NodeKey | null>(
    null,
  );
  const [fontSize, setFontSize] = useState<string>('14px');
  const [fontColor, setFontColor] = useState<string>('#000');
  const [bgColor, setBgColor] = useState<string>('#fff');
  const [fontFamily, setFontFamily] = useState<string>('Noto Sans');
  const [elementFormat, setElementFormat] = useState<ElementFormatType>('left');
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [modal, showModal] = useModal();
  const [isRTL, setIsRTL] = useState(false);
  const [codeLanguage, setCodeLanguage] = useState<string>('');
  const [isImageCaption, setIsImageCaption] = useState(false);
  const [isDefaultLanguageModalVisible, setIsDefaultLanguageModalVisible] =
    useState(false);
  const [isToolbarDisabled, setIsToolbarDisabled] = useState(false);
  const [previousKey, setPreviousKey] = useState<string | null>(null);

  const { setOption: setSetting } = useSettings();

  const routeParams = Links.Authorized.CustomPages.DesignerNewPage.useParams();
  const pageUrl = routeParams.url;
  const isNewPage = pageUrl === New;

  const showDefaultLanguageModal = () => {
    setIsDefaultLanguageModalVisible(true);
  };

  const closeDefaultLanguageModal = () => {
    setIsDefaultLanguageModalVisible(false);
  };

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      if (activeEditor !== editor && $isEditorIsNestedEditor(activeEditor)) {
        const rootElement = activeEditor.getRootElement();
        setIsImageCaption(
          !!rootElement?.parentElement?.classList.contains(
            'image-caption-container',
          ),
        );
      } else {
        setIsImageCaption(false);
      }

      const anchorNode = selection.anchor.getNode();
      const parentNode = anchorNode.getParent();
      const nonDefaultLanguage =
        pageDesignerContext?.defaultLanguage &&
        pageDesignerContext.defaultLanguage !==
          pageDesignerContext?.pageEditorLanguage;
      const isLinkNodeInsideTab =
        $isLocalizationLinkNode(parentNode) && parentNode.isInsideTab();
      const isTextNodeInsideTab =
        $isLocalizableTextNode(anchorNode) && anchorNode.isInsideTab();

      const parentKey = parentNode?.getKey();

      if (previousKey) {
        const previousElement = editor.getElementByKey(previousKey);
        previousElement?.classList.remove('focusedTab');
      }

      if (
        (!isNewPage && nonDefaultLanguage) ||
        isLinkNodeInsideTab ||
        $isTabItemNode(parentNode) ||
        isTextNodeInsideTab
      ) {
        if (
          parentKey &&
          ($isLocalizationLinkNode(parentNode) ||
            $isTabItemNode(parentNode) ||
            $isProtectedParagraphNode(parentNode))
        ) {
          const parentElement = editor.getElementByKey(parentKey);
          parentElement?.classList.add('focusedTab');
          setPreviousKey(parentKey);
        }

        setIsToolbarDisabled(true);
      } else {
        setIsToolbarDisabled(false);
      }

      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsSubscript(selection.hasFormat('subscript'));
      setIsSuperscript(selection.hasFormat('superscript'));
      setIsCode(selection.hasFormat('code'));
      setIsRTL($isParentElementRTL(selection));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      const tableNode = $findMatchingParent(node, $isTableNode);
      if ($isTableNode(tableNode)) {
        setRootType('table');
      } else {
        setRootType('root');
      }

      if (elementDOM !== null) {
        setSelectedElementKey(elementKey);
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(
            anchorNode,
            ListNode,
          );
          const type = parentList
            ? parentList.getListType()
            : element.getListType();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          if (type in blockTypeToBlockName) {
            setBlockType(type as keyof typeof blockTypeToBlockName);
          }
          if ($isCodeNode(element)) {
            const language =
              element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP;
            setCodeLanguage(
              language ? CODE_LANGUAGE_MAP[language] || language : '',
            );
            return;
          }
        }
      }
      // Handle buttons
      setFontColor(
        $getSelectionStyleValueForProperty(selection, 'color', '#000'),
      );
      setBgColor(
        $getSelectionStyleValueForProperty(
          selection,
          'background-color',
          '#fff',
        ),
      );
      setFontFamily(
        $getSelectionStyleValueForProperty(
          selection,
          'font-family',
          'Noto Sans',
        ),
      );
      let matchingParent;
      if ($isLinkNode(parent)) {
        // If node is a link, we need to fetch the parent paragraph node to set format
        matchingParent = $findMatchingParent(
          node,
          (parentNode) => $isElementNode(parentNode) && !parentNode.isInline(),
        );
      }

      // If matchingParent is a valid node, pass it's format type
      setElementFormat(
        $isElementNode(matchingParent)
          ? matchingParent.getFormatType()
          : $isElementNode(node)
          ? node.getFormatType()
          : parent?.getFormatType() || 'left',
      );
    }

    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      setFontSize(
        $getSelectionStyleValueForProperty(selection, 'font-size', '14px'),
      );
    }
  }, [
    activeEditor,
    editor,
    isNewPage,
    pageDesignerContext?.defaultLanguage,
    pageDesignerContext?.pageEditorLanguage,
    previousKey,
  ]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        setActiveEditor(newEditor);
        $updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, $updateToolbar]);

  useEffect(() => {
    activeEditor.getEditorState().read(() => {
      $updateToolbar();
    });
  }, [activeEditor, $updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      activeEditor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      activeEditor.registerCommand<boolean>(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      activeEditor.registerCommand<boolean>(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    );
  }, [$updateToolbar, activeEditor, editor]);

  useEffect(() => {
    return activeEditor.registerCommand(
      KEY_MODIFIER_COMMAND,
      (payload) => {
        const event: KeyboardEvent = payload;
        const { code, ctrlKey, metaKey } = event;

        if (code === 'KeyK' && (ctrlKey || metaKey)) {
          event.preventDefault();
          let url: string | null;
          if (!isLink) {
            setIsLinkEditMode(true);
            url = sanitizeUrl('https://');
          } else {
            setIsLinkEditMode(false);
            url = null;
          }
          return activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
        }
        return false;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [activeEditor, isLink, setIsLinkEditMode]);

  const applyStyleText = useCallback(
    (styles: Record<string, string>, skipHistoryStack?: boolean) => {
      activeEditor.update(
        () => {
          const selection = $getSelection();
          if (selection !== null) {
            $patchStyleText(selection, styles);
          }
        },
        skipHistoryStack ? { tag: 'historic' } : {},
      );
    },
    [activeEditor],
  );

  const clearFormatting = useCallback(() => {
    activeEditor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) || $isTableSelection(selection)) {
        const anchor = selection.anchor;
        const focus = selection.focus;
        const nodes = selection.getNodes();
        const extractedNodes = selection.extract();

        if (anchor.key === focus.key && anchor.offset === focus.offset) {
          return;
        }

        nodes.forEach((node, idx) => {
          // We split the first and last node by the selection
          // So that we don't format unselected text inside those nodes
          if ($isTextNode(node)) {
            // Use a separate variable to ensure TS does not lose the refinement
            let textNode = node;
            if (idx === 0 && anchor.offset !== 0) {
              textNode = textNode.splitText(anchor.offset)[1] || textNode;
            }
            if (idx === nodes.length - 1) {
              textNode = textNode.splitText(focus.offset)[0] || textNode;
            }
            /**
             * If the selected text has one format applied
             * selecting a portion of the text, could
             * clear the format to the wrong portion of the text.
             *
             * The cleared text is based on the length of the selected text.
             */
            // We need this in case the selected text only has one format
            const extractedTextNode = extractedNodes[0];
            if (nodes.length === 1 && $isTextNode(extractedTextNode)) {
              textNode = extractedTextNode;
            }

            if (textNode.__style !== '') {
              textNode.setStyle('');
            }
            if (textNode.__format !== 0) {
              textNode.setFormat(0);
              $getNearestBlockElementAncestorOrThrow(textNode).setFormat('');
            }
            node = textNode;
          } else if ($isHeadingNode(node) || $isQuoteNode(node)) {
            node.replace($createParagraphNode(), true);
          } else if ($isDecoratorBlockNode(node)) {
            node.setFormat('');
          }
        });
      }
    });
  }, [activeEditor]);

  const onFontColorSelect = useCallback(
    (value: string, skipHistoryStack: boolean) => {
      applyStyleText({ color: value }, skipHistoryStack);
    },
    [applyStyleText],
  );

  const onBgColorSelect = useCallback(
    (value: string, skipHistoryStack: boolean) => {
      applyStyleText({ 'background-color': value }, skipHistoryStack);
    },
    [applyStyleText],
  );

  const insertLink = useCallback(() => {
    if (!isLink) {
      activeEditor.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const focusNode = getSelectedNode(selection);
          if (
            $isLocalizableTextNode(focusNode) &&
            focusNode.getTextContent().trim()
          ) {
            setIsLinkEditMode(true);
            setSetting('isCreatingLink', true, false);
          }
        }
      });
    } else {
      setIsLinkEditMode(false);
      setSetting('isCreatingLink', false, false);
      activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [activeEditor, isLink, setIsLinkEditMode]);

  const onCodeLanguageSelect = useCallback(
    (value: string) => {
      activeEditor.update(() => {
        if (selectedElementKey !== null) {
          const node = $getNodeByKey(selectedElementKey);
          if ($isCodeNode(node)) {
            node.setLanguage(value);
          }
        }
      });
    },
    [activeEditor, selectedElementKey],
  );

  const exportJSON = async () => {
    const queryKey = CmsPageQuery.getDetailsByUrlQueryKey({
      url: pageUrl,
      getActiveVersion: true,
    });
    const cachedData = queryClient.getQueryData(queryKey) as CmsPageDto;

    const titleTranslations: Record<Language, string | null> = {
      [Language.En]: null,
      [Language.De]: null,
      [Language.Fr]: null,
      [Language.Es]: null,
    };

    Object.keys(titleTranslations).forEach((language) => {
      if (language === cachedData.defaultLanguage) {
        titleTranslations[language] = cachedData.title;
      } else {
        const translation = cachedData.titleTranslations.find(
          (titleTranslation) => titleTranslation.language === language,
        )?.translatedText;

        titleTranslations[language as Language] = translation ?? null;
      }

      if (pageDesignerContext?.pageEditorLanguage) {
        titleTranslations[pageDesignerContext.pageEditorLanguage] =
          pageDesignerContext?.pageTitle;
      }
    });

    const jsonState = {
      ...editor.getEditorState().toJSON(),
      title: titleTranslations,
    };

    const jsonString = JSON.stringify(jsonState, null, 2);

    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${pageDesignerContext?.pageTitle || 'LEOworld'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const importJSON = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      try {
        const jsonString = reader.result as string;
        const parsedJSON = JSON.parse(jsonString);
        const importedState = editor.parseEditorState(parsedJSON);
        const currentLanguage = pageDesignerContext?.pageEditorLanguage;
        const newTranslationsById: Record<string, string> = {};

        if (!currentLanguage) {
          return;
        }

        const newTitle = parsedJSON.title[currentLanguage];

        if (newTitle && newTitle !== pageDesignerContext?.pageTitle) {
          pageDesignerContext?.setTitle(newTitle);
        }

        importedState.read(() => {
          const root = $getRoot();

          const rememberTranslations = (node: LexicalNode) => {
            if (!$isElementNode(node)) return;

            for (const child of node.getChildren()) {
              if ($isLocalizableTextNode(child)) {
                const id = child.__id;
                const newTranslation = child.__translations[currentLanguage];
                if (id && newTranslation) {
                  newTranslationsById[id] = newTranslation;
                }
              } else {
                rememberTranslations(child);
              }
            }
          };

          rememberTranslations(root);
        });

        editor.update(() => {
          const root = $getRoot();

          const applyNewTranslations = (node: LexicalNode) => {
            if (!$isElementNode(node)) return;

            for (const child of node.getChildren()) {
              if ($isLocalizableTextNode(child)) {
                const id = child.__id;

                if (newTranslationsById[id]) {
                  const currentTranslation =
                    child.__translations[currentLanguage];
                  const newTranslation = newTranslationsById[id];

                  if (currentTranslation !== newTranslation) {
                    child.setTranslations(currentLanguage, newTranslation);
                    child.setTextContent(newTranslation);
                    child.removeLanguageManuallyChanged(currentLanguage);
                    child.addLanguageManuallyChanged(currentLanguage);

                    if (currentLanguage === child.__originalLanguage) {
                      child.resetLanguagesTranslatedByAI();
                    }
                  }
                }
              } else {
                applyNewTranslations(child);
              }
            }
          };

          applyNewTranslations(root);
        });
      } catch (e) {}
    };

    reader.readAsText(file);
    event.target.value = '';
  };

  const canViewerSeeInsertDropdown = !isImageCaption;
  const canViewerSeeInsertCodeButton = !isImageCaption;
  const isVersionsModeActive = pageDesignerContext?.isVersionsModeActive;

  if (!isLexicalEditable && !pageDesignerContext?.isVersionsModeActive) {
    return <></>;
  }

  return (
    <div className="toolbar">
      <div className={clsx(styles.rowContainer, styles.spaceBetween)}>
        <div
          className={clsx(
            styles.rowContainer,
            (isToolbarDisabled || isVersionsModeActive) && styles.opacity50,
          )}
        >
          <button
            disabled={!canUndo || isToolbarDisabled}
            onClick={() => {
              activeEditor.dispatchCommand(UNDO_COMMAND, undefined);
            }}
            title={`${i18n.t('undo')} ${IS_APPLE ? '(⌘Z)' : '(Ctrl+Z)'}`}
            type="button"
            className="toolbar-item spaced"
            aria-label="Undo"
          >
            <i className="format undo" />
          </button>
          <button
            disabled={!canRedo || isToolbarDisabled}
            onClick={() => {
              activeEditor.dispatchCommand(REDO_COMMAND, undefined);
            }}
            title={`${i18n.t('redo')} ${IS_APPLE ? '(⇧⌘Z)' : '(Ctrl+Y)'}`}
            type="button"
            className="toolbar-item"
            aria-label="Redo"
          >
            <i className="format redo" />
          </button>
          <Divider />
          {blockType in blockTypeToBlockName && activeEditor === editor && (
            <>
              <BlockFormatDropDown
                blockType={blockType}
                rootType={rootType}
                editor={activeEditor}
              />
              <Divider />
            </>
          )}
          {blockType === 'code' ? (
            <DropDown
              buttonClassName="toolbar-item code-language"
              buttonLabel={getLanguageFriendlyName(codeLanguage)}
              buttonAriaLabel="Select language"
            >
              {CODE_LANGUAGE_OPTIONS.map(([value, name]) => {
                return (
                  <DropDownItem
                    className={`item ${dropDownActiveClass(
                      value === codeLanguage,
                    )}`}
                    onClick={() => onCodeLanguageSelect(value)}
                    key={value}
                  >
                    <span className="text">{name}</span>
                  </DropDownItem>
                );
              })}
            </DropDown>
          ) : (
            <>
              <FontDropDown
                style={'font-family'}
                value={fontFamily}
                editor={activeEditor}
              />
              <Divider />
              <FontSize
                disabled={false}
                selectionFontSize={fontSize.slice(0, -2)}
                editor={activeEditor}
              />
              <Divider />
              <button
                onClick={() => {
                  activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
                }}
                className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
                title={`${i18n.t('bold')} ${IS_APPLE ? '(⌘B)' : '(Ctrl+B)'}`}
                type="button"
                aria-label={`Format text as bold. Shortcut: ${
                  IS_APPLE ? '⌘B' : 'Ctrl+B'
                }`}
              >
                <i className="format bold" />
              </button>
              <button
                onClick={() => {
                  activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
                }}
                className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
                title={`${i18n.t('italic')} ${IS_APPLE ? '(⌘I)' : '(Ctrl+I)'}`}
                type="button"
                aria-label={`Format text as italics. Shortcut: ${
                  IS_APPLE ? '⌘I' : 'Ctrl+I'
                }`}
              >
                <i className="format italic" />
              </button>
              <button
                onClick={() => {
                  activeEditor.dispatchCommand(
                    FORMAT_TEXT_COMMAND,
                    'underline',
                  );
                }}
                className={
                  'toolbar-item spaced ' + (isUnderline ? 'active' : '')
                }
                title={`${i18n.t('underline')} ${
                  IS_APPLE ? '(⌘U)' : '(Ctrl+U)'
                }`}
                type="button"
                aria-label={`Format text to underlined. Shortcut: ${
                  IS_APPLE ? '⌘U' : 'Ctrl+U'
                }`}
              >
                <i className="format underline" />
              </button>
              {/*{canViewerSeeInsertCodeButton && (*/}
              {/*  <button*/}
              {/*    onClick={() => {*/}
              {/*      activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');*/}
              {/*    }}*/}
              {/*    className={*/}
              {/*      'toolbar-item spaced ' + (isCode ? 'active' : '')*/}
              {/*    }*/}
              {/*    title={i18n.t('insert_code_block')}*/}
              {/*    type="button"*/}
              {/*    aria-label="Insert code block"*/}
              {/*  >*/}
              {/*    <i className="format code" />*/}
              {/*  </button>*/}
              {/*)}*/}
              <button
                onClick={insertLink}
                className={'toolbar-item spaced ' + (isLink ? 'active' : '')}
                aria-label="Insert link"
                title={i18n.t('insert_link')}
                type="button"
              >
                <i className="format link" />
              </button>
              <DropdownColorPicker
                buttonClassName="toolbar-item color-picker"
                buttonAriaLabel="Formatting text color"
                buttonIconClassName="icon font-color"
                color={fontColor}
                onChange={onFontColorSelect}
                title="text color"
              />
              <DropdownColorPicker
                buttonClassName="toolbar-item color-picker"
                buttonAriaLabel="Formatting background color"
                buttonIconClassName="icon bg-color"
                color={bgColor}
                onChange={onBgColorSelect}
                title="bg color"
              />
              <DropDown
                buttonClassName="toolbar-item spaced"
                buttonLabel=""
                buttonAriaLabel="Formatting options for additional text styles"
                buttonIconClassName="icon dropdown-more"
              >
                <DropDownItem
                  onClick={() => {
                    activeEditor.dispatchCommand(
                      FORMAT_TEXT_COMMAND,
                      'strikethrough',
                    );
                  }}
                  className={'item ' + dropDownActiveClass(isStrikethrough)}
                  title={i18n.t('strikethrough')}
                  aria-label="Format text with a strikethrough"
                >
                  <i className="icon strikethrough" />
                  <span className="text">{i18n.t('strikethrough')}</span>
                </DropDownItem>
                <DropDownItem
                  onClick={() => {
                    activeEditor.dispatchCommand(
                      FORMAT_TEXT_COMMAND,
                      'subscript',
                    );
                  }}
                  className={'item ' + dropDownActiveClass(isSubscript)}
                  title={i18n.t('subscript')}
                  aria-label="Format text with a subscript"
                >
                  <i className="icon subscript" />
                  <span className="text">{i18n.t('subscript')}</span>
                </DropDownItem>
                <DropDownItem
                  onClick={() => {
                    activeEditor.dispatchCommand(
                      FORMAT_TEXT_COMMAND,
                      'superscript',
                    );
                  }}
                  className={'item ' + dropDownActiveClass(isSuperscript)}
                  title={i18n.t('superscript')}
                  aria-label="Format text with a superscript"
                >
                  <i className="icon superscript" />
                  <span className="text">{i18n.t('superscript')}</span>
                </DropDownItem>
                <Divider />
                <DropDownItem
                  onClick={clearFormatting}
                  className="item deleteStyles"
                  title={i18n.t('clear_formatting')}
                  aria-label="Clear all text formatting"
                >
                  <Reset style={{ marginRight: 8 }} />
                  <span className="text">{i18n.t('clear_formatting')}</span>
                </DropDownItem>
              </DropDown>
              {canViewerSeeInsertDropdown && (
                <>
                  <Divider />
                  <DropDown
                    buttonClassName="toolbar-item spaced"
                    buttonLabel={i18n.t('insert')}
                    buttonAriaLabel="Insert specialized editor node"
                    buttonIconClassName="icon plus"
                  >
                    <DropDownItem
                      onClick={() => {
                        showModal(
                          `${i18n.t('insert')} ${i18n.t('table')}`,
                          (onClose) => (
                            <InsertTableDialog
                              activeEditor={activeEditor}
                              onClose={onClose}
                            />
                          ),
                          false,
                          styles.width350,
                        );
                      }}
                      className="item"
                    >
                      <i className="icon table" />
                      <span className="text">{i18n.t('table')}</span>
                    </DropDownItem>
                    <DropDownItem
                      onClick={() => {
                        showModal(
                          `${i18n.t('insert')} ${i18n.t('firmware')} ${i18n.t(
                            'table',
                          )}`,
                          (onClose) => (
                            <InsertFirmwareTableDialog
                              activeEditor={activeEditor}
                              onClose={onClose}
                            />
                          ),
                          false,
                          styles.width350,
                        );
                      }}
                      className="item"
                    >
                      <i className="icon firmware" />
                      <span className="text">{i18n.t('firmware')}</span>
                    </DropDownItem>
                    <Divider />
                    <DropDownItem
                      onClick={() => {
                        showModal(
                          `${i18n.t('insert')} ${i18n.t('tabs')}`,
                          (onClose) => (
                            <InsertLayoutDialog
                              activeEditor={activeEditor}
                              onClose={onClose}
                              isTab={true}
                            />
                          ),
                          false,
                          styles.width350,
                        );
                      }}
                      className="item"
                    >
                      <i className="icon columns" />
                      <span className="text">{i18n.t('tabs')}</span>
                    </DropDownItem>
                    {/*<DropDownItem*/}
                    {/*  onClick={() => {*/}
                    {/*    showModal(*/}
                    {/*      `${i18n.t('insert')} ${i18n.t('columns')}`,*/}
                    {/*      (onClose) => (*/}
                    {/*        <InsertLayoutDialog*/}
                    {/*          activeEditor={activeEditor}*/}
                    {/*          onClose={onClose}*/}
                    {/*        />*/}
                    {/*      ),*/}
                    {/*      false,*/}
                    {/*      styles.width350,*/}
                    {/*    );*/}
                    {/*  }}*/}
                    {/*  className="item"*/}
                    {/*>*/}
                    {/*  <i className="icon tabs" />*/}
                    {/*  <span className="text">{i18n.t('columns')}</span>*/}
                    {/*</DropDownItem>*/}
                    <DropDownItem
                      onClick={() => {
                        editor.dispatchCommand(
                          INSERT_COLLAPSIBLE_COMMAND,
                          undefined,
                        );
                      }}
                      className="item"
                    >
                      <i className="icon caret-right" />
                      <span className="text">
                        {i18n.t('collapsible_container')}
                      </span>
                    </DropDownItem>
                    <Divider />
                    <DropDownItem
                      onClick={() => {
                        showModal(
                          `${i18n.t('insert')} ${i18n.t('image')}`,
                          (onClose) => (
                            <InsertImageDialog
                              activeEditor={activeEditor}
                              onClose={onClose}
                            />
                          ),
                          false,
                          styles.width400,
                        );
                      }}
                      className="item"
                    >
                      <i className="icon image" />
                      <span className="text">{i18n.t('image')}</span>
                    </DropDownItem>
                    {/*<DropDownItem*/}
                    {/*  onClick={() => {*/}
                    {/*    showModal(*/}
                    {/*      `${i18n.t('insert')} ${i18n.t('inline_image')}`,*/}
                    {/*      (onClose) => (*/}
                    {/*        <InsertInlineImageDialog*/}
                    {/*          activeEditor={activeEditor}*/}
                    {/*          onClose={onClose}*/}
                    {/*        />*/}
                    {/*      ),*/}
                    {/*      false,*/}
                    {/*      styles.width400,*/}
                    {/*    );*/}
                    {/*  }}*/}
                    {/*  className="item"*/}
                    {/*>*/}
                    {/*  <i className="icon inline-image" />*/}
                    {/*  <span className="text">{i18n.t('inline_image')}</span>*/}
                    {/*</DropDownItem>*/}
                    {EmbedConfigs.map((embedConfig) => (
                      <DropDownItem
                        key={embedConfig.type}
                        onClick={() => {
                          activeEditor.dispatchCommand(
                            INSERT_EMBED_COMMAND,
                            embedConfig.type,
                          );
                        }}
                        className="item"
                      >
                        {embedConfig.icon}
                        <span className="text">
                          {tDynamic(i18n, embedConfig.contentName)}
                        </span>
                      </DropDownItem>
                    ))}
                    {/*<Divider />*/}
                    {/*<DropDownItem*/}
                    {/*  onClick={() => {*/}
                    {/*    showModal(*/}
                    {/*      `${i18n.t('insert')} ${i18n.t('poll')}`,*/}
                    {/*      (onClose) => (*/}
                    {/*        <InsertPollDialog*/}
                    {/*          activeEditor={activeEditor}*/}
                    {/*          onClose={onClose}*/}
                    {/*        />*/}
                    {/*      ),*/}
                    {/*    );*/}
                    {/*  }}*/}
                    {/*  className="item"*/}
                    {/*>*/}
                    {/*  <i className="icon poll" />*/}
                    {/*  <span className="text">{i18n.t('poll')}</span>*/}
                    {/*</DropDownItem>*/}
                    {/*<DropDownItem*/}
                    {/*  onClick={() => {*/}
                    {/*    editor.update(() => {*/}
                    {/*      const root = $getRoot();*/}
                    {/*      const stickyNode = $createStickyNode(0, 0);*/}
                    {/*      root.append(stickyNode);*/}
                    {/*    });*/}
                    {/*  }}*/}
                    {/*  className="item"*/}
                    {/*>*/}
                    {/*  <i className="icon sticky" />*/}
                    {/*  <span className="text">{i18n.t('sticky_note')}</span>*/}
                    {/*</DropDownItem>*/}
                    {/*<Divider />*/}
                    {/*<DropDownItem*/}
                    {/*  onClick={() => {*/}
                    {/*    activeEditor.dispatchCommand(*/}
                    {/*      INSERT_HORIZONTAL_RULE_COMMAND,*/}
                    {/*      undefined,*/}
                    {/*    );*/}
                    {/*  }}*/}
                    {/*  className="item"*/}
                    {/*>*/}
                    {/*  <i className="icon horizontal-rule" />*/}
                    {/*  <span className="text">*/}
                    {/*    {i18n.t('horizontal_rule')}*/}
                    {/*  </span>*/}
                    {/*</DropDownItem>*/}
                    {/*<DropDownItem*/}
                    {/*  onClick={() => {*/}
                    {/*    activeEditor.dispatchCommand(*/}
                    {/*      INSERT_PAGE_BREAK,*/}
                    {/*      undefined,*/}
                    {/*    );*/}
                    {/*  }}*/}
                    {/*  className="item"*/}
                    {/*>*/}
                    {/*  <i className="icon page-break" />*/}
                    {/*  <span className="text">{i18n.t('page_break')}</span>*/}
                    {/*</DropDownItem>*/}
                  </DropDown>
                </>
              )}
            </>
          )}
          <Divider />
          <ElementFormatDropdown
            disabled={false}
            value={elementFormat}
            editor={activeEditor}
            isRTL={isRTL}
          />
        </div>
        {!isNewPage ? (
          pageDesignerContext?.defaultLanguage ? (
            <div className={clsx(styles.rowContainer, styles.columnGap4)}>
              {FeatureFlags.isPageEditorAIEnabled() &&
                pageDesignerContext.defaultLanguage !==
                  pageDesignerContext.pageEditorLanguage && (
                  <>
                    {isVersionsModeActive ? (
                      <AITranslationIconReact
                        className={clsx(styles.opacity50, styles.marginRight16)}
                      />
                    ) : (
                      <Tip
                        tooltipText={i18n.t('ai_translate')}
                        customImage={AITranslationIcon}
                        customImageClassname={styles.exportButton}
                        className={styles.noBackground}
                        onClick={pageDesignerContext.handleTranslate}
                      />
                    )}
                  </>
                )}
              {isVersionsModeActive ? (
                <ExportIconReact
                  className={clsx(
                    styles.opacity50,
                    styles.marginRight8,
                    styles.reverted,
                  )}
                />
              ) : (
                <Tip
                  tooltipBody={<ImportTooltipBody />}
                  customImage={ExportIcon}
                  onClick={exportJSON}
                  customImageClassname={clsx(
                    styles.exportButton,
                    styles.reverted,
                  )}
                  className={clsx(
                    styles.noBackground,
                    isVersionsModeActive && styles.opacity50,
                  )}
                />
              )}
              <input
                className={styles.displayNone}
                type={'file'}
                accept={'.json'}
                id={'json-upload'}
                onChange={importJSON}
              />
              <label
                htmlFor={!isVersionsModeActive ? 'json-upload' : undefined}
                className={clsx(
                  styles.height36,
                  isVersionsModeActive && styles.opacity50,
                )}
              >
                <Tip
                  tooltipBody={<ExportTooltipBody />}
                  customImage={ExportIcon}
                  customImageClassname={clsx(styles.exportButton)}
                  className={styles.noBackground}
                  tooltipClassName={styles.marginLeftMinus40}
                />
              </label>
              <LanguageSelector />
            </div>
          ) : (
            <div
              className={clsx(
                styles.translateButton,
                isVersionsModeActive
                  ? styles.opacity50
                  : styles.translateButtonActions,
              )}
              onClick={
                isVersionsModeActive ? undefined : showDefaultLanguageModal
              }
            >
              <TranslateIcon />
              <div>{i18n.t('translate')}</div>
            </div>
          )
        ) : null}
        {isDefaultLanguageModalVisible && (
          <DefaultLanguageModal closeModal={closeDefaultLanguageModal} />
        )}
      </div>
      {modal}
    </div>
  );
}
